var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { refVersionAtOrAbove } from "@hyper-hyper-space/hhs3_mvt";
import { labelForKeyId, resolveObserveAuthor, } from "./authz_suggest.js";
import { KeyUnlockDeclinedError } from "./prompts.js";
export class RefAutoUpdateSkippedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RefAutoUpdateSkippedError';
    }
}
export function extractRefUpdateTrigger(bound) {
    switch (bound.kind) {
        case 'insert':
        case 'update':
        case 'delete':
            return { sourceGroupId: bound.table.groupId, author: bound.author };
        case 'bundle':
        case 'update-schema':
            return { sourceGroupId: bound.group.id, author: bound.author };
        default:
            return undefined;
    }
}
export function findObservers(session, foreignGroupId) {
    const observers = [];
    for (const root of session.workspace.roots.list('group')) {
        if (root.object === undefined)
            continue;
        const observer = root.object;
        for (const [bindingName, boundId] of Object.entries(observer.getBindings())) {
            if (boundId === foreignGroupId) {
                observers.push({ observerId: root.id, observer, bindingName });
            }
        }
    }
    return observers;
}
function observerDisplayName(session, observerGroupId) {
    var _a;
    const name = (_a = session.workspace.roots.get(observerGroupId)) === null || _a === void 0 ? void 0 : _a.name;
    if (name !== undefined && name.length > 0)
        return name;
    return observerGroupId.slice(0, 8);
}
export function formatRefAutoUpdateNotice(session, observerGroupId, entryHash) {
    const group = observerDisplayName(session, observerGroupId);
    const hash = `#${entryHash.slice(0, 8)}`;
    return `updated ref on ${group} to ${hash}`;
}
export function formatRefAutoUpdateFailure(session, observerGroupId, message) {
    const group = observerDisplayName(session, observerGroupId);
    return `ref update on ${group} failed: ${message}`;
}
export function formatRefAutoUpdateSkipped(session, observerGroupId, rejected) {
    const group = observerDisplayName(session, observerGroupId);
    if (rejected.length === 0)
        return `ref update on ${group} skipped: no author configured`;
    const labels = rejected.map((c) => `$${c.label}`).join(', ');
    return `ref update on ${group} skipped: ${labels} not authorized`;
}
function loadForeignGroup(session, foreignGroupId) {
    return __awaiter(this, void 0, void 0, function* () {
        const root = session.workspace.roots.get(foreignGroupId);
        if ((root === null || root === void 0 ? void 0 : root.object) !== undefined)
            return root.object;
        const object = yield session.workspace.replica.getObject(foreignGroupId);
        if (object === undefined)
            throw new Error(`Group '${foreignGroupId}' is not loaded`);
        return object;
    });
}
function isAlreadyObserved(observer, foreignGroupId, targetVersion, foreignGroup) {
    return __awaiter(this, void 0, void 0, function* () {
        const view = yield observer.getView();
        const current = yield view.resolveRefVersion(foreignGroupId);
        const foreignDag = yield foreignGroup.getCausalDag();
        return refVersionAtOrAbove(foreignDag, current, targetVersion);
    });
}
function pairKey(observerId, foreignId) {
    return `${observerId}:${foreignId}`;
}
function observeWithResolvedAuthor(session, observer, observerId, bindingName, foreignGroupId, targetVersion, triggerAuthor, mode, auth) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const impl = observer;
        const refAt = targetVersion;
        const refFrom = targetVersion;
        if (impl.observeGateFor(foreignGroupId) === undefined) {
            return observer.observe(bindingName, targetVersion, triggerAuthor);
        }
        const preferred = [triggerAuthor, yield session.currentAuthor()];
        const resolution = yield resolveObserveAuthor(session, impl, foreignGroupId, refAt, refFrom, preferred, { scanKeystore: mode === 'auto' });
        if (resolution.identity !== undefined) {
            return observer.observe(bindingName, targetVersion, resolution.identity);
        }
        if (resolution.locked !== undefined && ((_a = auth === null || auth === void 0 ? void 0 : auth.canPrompt) === null || _a === void 0 ? void 0 : _a.call(auth)) === true && auth.unlockIdentity !== undefined) {
            const label = resolution.locked.label;
            const authorLabel = labelForKeyId(session, resolution.locked.keyId);
            if (auth.confirmRefUpdateUnlock !== undefined) {
                yield auth.confirmRefUpdateUnlock(observerDisplayName(session, observerId), authorLabel);
            }
            const identity = yield auth.unlockIdentity(label);
            if (identity === undefined)
                throw new KeyUnlockDeclinedError(authorLabel);
            return observer.observe(bindingName, targetVersion, identity);
        }
        if (resolution.locked !== undefined) {
            throw new Error(`needs ${labelForKeyId(session, resolution.locked.keyId)} (locked)`);
        }
        if (mode === 'self') {
            throw new RefAutoUpdateSkippedError(formatRefAutoUpdateSkipped(session, observerId, (_b = resolution.rejected) !== null && _b !== void 0 ? _b : []));
        }
        throw new Error('no keystore identity satisfies gate');
    });
}
export function propagateRefUpdates(session, sourceGroupId, author, auth) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const mode = session.refAutoUpdate;
        if (mode === 'off')
            return [];
        const events = [];
        const visitedPairs = new Set();
        const queue = [sourceGroupId];
        while (queue.length > 0) {
            const foreignGroupId = queue.shift();
            const foreignGroup = yield loadForeignGroup(session, foreignGroupId);
            const targetVersion = yield (yield foreignGroup.getScopedDag()).getFrontier();
            for (const { observerId, observer, bindingName } of findObservers(session, foreignGroupId)) {
                const key = pairKey(observerId, foreignGroupId);
                if (visitedPairs.has(key))
                    continue;
                try {
                    if (yield isAlreadyObserved(observer, foreignGroupId, targetVersion, foreignGroup)) {
                        visitedPairs.add(key);
                        continue;
                    }
                    const entryHash = yield observeWithResolvedAuthor(session, observer, observerId, bindingName, foreignGroupId, targetVersion, author, mode, auth);
                    visitedPairs.add(key);
                    const message = formatRefAutoUpdateNotice(session, observerId, entryHash);
                    const event = { kind: 'updated', observerGroupId: observerId, message, entryHash };
                    events.push(event);
                    (_a = auth === null || auth === void 0 ? void 0 : auth.onProgress) === null || _a === void 0 ? void 0 : _a.call(auth, message);
                    queue.push(observerId);
                }
                catch (e) {
                    visitedPairs.add(key);
                    if (e instanceof RefAutoUpdateSkippedError) {
                        const event = { kind: 'skipped', observerGroupId: observerId, message: e.message };
                        events.push(event);
                        (_b = auth === null || auth === void 0 ? void 0 : auth.onProgress) === null || _b === void 0 ? void 0 : _b.call(auth, e.message);
                        continue;
                    }
                    const errMessage = e instanceof Error ? e.message : String(e);
                    const message = formatRefAutoUpdateFailure(session, observerId, errMessage);
                    const event = { kind: 'failed', observerGroupId: observerId, message };
                    events.push(event);
                    (_c = auth === null || auth === void 0 ? void 0 : auth.onProgress) === null || _c === void 0 ? void 0 : _c.call(auth, message);
                }
            }
        }
        return events;
    });
}
